import React, { useState } from 'react';
import { ArrowUpRight, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onSelect: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onSelect }) => {
  const { i18n, t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const description = product.description[i18n.language.startsWith('en') ? 'en' : 'fr'];
  // product.image is the admin-chosen cover — it must win over images[0].
  // images[] can (and often does, for older/seeded products) still contain
  // a stale copy of a PREVIOUS cover, since replacing the cover in the admin
  // form doesn't retroactively remove it from the gallery list.
  const image = product.image || product.images?.[0];

  return (
    <motion.article
      whileHover={{ y: -5 }}
      transition={{ duration: 0.25 }}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-coffee-200/10 bg-cream-50/90 shadow-soft transition-shadow hover:shadow-xl hover:shadow-coffee-200/10"
      onClick={() => onSelect(product)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[1.15/1] overflow-hidden bg-cream-100">
        <img src={image} alt={product.name} className={`h-full w-full object-cover transition-transform duration-700 ${isHovered ? 'scale-105' : 'scale-100'}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-coffee-200/50 via-transparent to-transparent" />
        <div className="absolute left-4 top-4 rounded-full bg-cream-50/90 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-coffee-200 backdrop-blur-sm">
          {product.grade ? `${t('products.grade', 'Grade')} ${product.grade}` : t(`products.${product.category}.title`, product.category)}
        </div>
        <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 text-xs font-semibold text-cream-50"><MapPin className="h-3.5 w-3.5" />Madagascar</span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-2xl font-bold text-coffee-200">{product.name}</h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-coffee-100">{description}</p>
        <div className="mt-5 grid grid-cols-2 gap-2 border-t border-coffee-200/10 pt-4">
          {product.specifications.slice(0, 2).map((spec) => (
            <div key={spec.name}><p className="text-[10px] font-bold uppercase tracking-wider text-coffee-100/60">{spec.name}</p><p className="mt-1 text-sm font-bold text-coffee-200">{spec.value}{spec.unit}</p></div>
          ))}
        </div>
        <button type="button" className="mt-6 inline-flex items-center justify-between border-t border-coffee-200/10 pt-4 text-sm font-bold text-forest-200 transition-colors group-hover:text-coffee-200" onClick={(event) => { event.stopPropagation(); onSelect(product); }}>
          {t('products.details', 'Voir la fiche')}
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-forest-200/10 transition-transform group-hover:translate-x-1"><ArrowUpRight className="h-4 w-4" /></span>
        </button>
      </div>
    </motion.article>
  );
};

export default ProductCard;
